import Form from 'react-bootstrap/Form';
import { useEffect, useRef, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { bulkEditActions } from '@/enums/bulk-edit-actions';
import { FORM_TEMPLATES } from '@certificate_hero/enums';
import { coverageFlowStyles } from '@/enums/coverage-flow-styles';
/**
 * @param {object} props
 * @param {LobTemplateEntity} props.data
 */

const STATUSES = {
  UNCHECKED: 'unchecked',
  CHECKED: 'checked',
  INTERMEDIATE: 'intermediate',
};

export const LobCertOnlyCheckbox = ({
  data: lob,
  field,
  onCertOnlyChange,
  formValuesLobs,
}) => {
  const ref = useRef(null);
  const countName = field === 'wos' ? 'wosCount' : 'aiCount';

  const allCertsHaveField = lob.certCount === lob[countName];
  const noCertsHaveField = lob[countName] === 0;

  const actionsQueuedForLob = useMemo(() => {
    return formValuesLobs
      .filter(formLob => formLob.id === lob.id)
      .map(formLob => formLob.action);
  }, [formValuesLobs, lob?.id]);

  const isOnForm = useMemo(() => {
    const template = FORM_TEMPLATES.find(form => form.id === lob.formId);

    if (!template) {
      return false;
    }

    const coverageFamily = field === 'wos' ? 'WVR_OF_SUBRO' : 'ADDL_INSD';

    const lobFieldsMapping = template?.fieldMappings?.lobs?.find(mapping =>
      mapping.lobSymbols.includes(lob.lobSymbol)
    );

    if (!lobFieldsMapping) {
      return false;
    }

    if (
      lob.formId === 'Acord 27' &&
      coverageFamily === 'ADDL_INSD' &&
      lob.lobSymbol === 'PROP'
    ) {
      return true;
    }

    return lobFieldsMapping.fields.find(
      mapping =>
        mapping.coverageFamily === coverageFamily &&
        mapping.flowStyle === coverageFlowStyles.CERT_ONLY
    );
  }, [lob.formId, field, lob.lobSymbol]);

  const checkStatus = useMemo(() => {
    const initialStatus = noCertsHaveField
      ? STATUSES.UNCHECKED
      : allCertsHaveField
      ? STATUSES.CHECKED
      : STATUSES.INTERMEDIATE;

    if (actionsQueuedForLob.includes(bulkEditActions.REMOVE)) {
      return STATUSES.UNCHECKED;
    }

    if (actionsQueuedForLob.includes(bulkEditActions.UPDATE_CERT_ONLY)) {
      const lobChange = formValuesLobs.find(
        formLob =>
          formLob.id === lob.id &&
          formLob.action === bulkEditActions.UPDATE_CERT_ONLY
      );

      if (lobChange?.[field] !== undefined && lobChange[field] !== null) {
        return lobChange[field] ? STATUSES.CHECKED : STATUSES.UNCHECKED;
      }
    }

    return initialStatus;
  }, [
    noCertsHaveField,
    allCertsHaveField,
    actionsQueuedForLob,
    formValuesLobs,
    lob,
    field,
  ]);

  useEffect(() => {
    if (!isOnForm) {
      return;
    }

    if (checkStatus === STATUSES.INTERMEDIATE) {
      ref.current.indeterminate = true;
    } else {
      ref.current.indeterminate = false;
    }
  }, [checkStatus, isOnForm]);

  const name = field === 'wos' ? 'Waiver of Subrogation' : 'Additional Insured';

  const certWord = lob[countName] === 1 ? 'cert' : 'certs';

  let tooltipText = `${lob[countName]} out of ${lob.certCount} selected certs with this LOB has ${name}`;

  if (lob[countName] === 0) {
    tooltipText = `No selected cert with this LOB has ${name}`;
  }

  if (lob[countName] === lob.certCount) {
    tooltipText = `Every selected cert with this LOB has ${name}`;
  }

  if (lob.certCount === 0) {
    tooltipText = `No selected ${certWord} with this LOB`;
  }

  if (!isOnForm) {
    tooltipText = `No ${name} field for ${lob.lobSymbol} on ${lob.formId}`;
  }

  const setChecked = () => {
    if (actionsQueuedForLob.includes(bulkEditActions.APPEND)) {
      lob[field] = true;
    } else {
      lob[field] = allCertsHaveField ? null : true;
    }
  };

  const setUnchecked = () => {
    lob[field] = noCertsHaveField ? null : false;
  };

  const setIntermediate = () => {
    lob[field] = null;
  };

  /**
   * @param {boolean} newValue
   */
  const updateCheckbox = () => {
    const allowIntermediate =
      lob[countName] > 0 && lob[countName] < lob.certCount;

    if (checkStatus === STATUSES.CHECKED) {
      setUnchecked();
    } else if (checkStatus === STATUSES.INTERMEDIATE) {
      setChecked();
    } else if (allowIntermediate) {
      setIntermediate();
    } else {
      setChecked();
    }

    onCertOnlyChange(lob, field, lob[field]);
  };

  return (
    <Form>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{tooltipText}</Tooltip>}
      >
        <div key={`default-checkbox`} className="mb-3">
          <Form.Check
            type={'checkbox'}
            checked={
              checkStatus === STATUSES.CHECKED ||
              checkStatus === STATUSES.INTERMEDIATE
            }
            disabled={
              !isOnForm ||
              (lob.certCount === 0 &&
                !actionsQueuedForLob.includes(bulkEditActions.APPEND)) ||
              actionsQueuedForLob.includes(bulkEditActions.REMOVE)
            }
            ref={ref}
            id={`default-checkbox`}
            onChange={() => {
              updateCheckbox();
            }}
          />
        </div>
      </OverlayTrigger>
    </Form>
  );
};
